import type { Product } from "@/types";

const SHOE_SIZES = ["34", "35", "36", "37", "38", "39", "40"];
const ACCESSORY_SIZES = ["Único"];

/**
 * Catálogo inicial da Auréa — 40 produtos originais.
 *
 * 30 calçados, 6 bolsas e 4 acessórios (dentro do foco mínimo: 75% calçados,
 * até 15% bolsas, até 10% cintos e semijoias).
 *
 * REGRA DE IMAGEM ÚNICA: cada produto tem fotografia própria e cada cor tem a
 * sua própria imagem. Nenhuma URL é reutilizada entre produtos diferentes — o
 * único reaproveitamento permitido é a mesma foto aparecer no card e na página
 * de detalhe do MESMO produto. Todas usam sequência exclusiva e forçam geração
 * nova (nocache) para nunca repetir a foto de outro item.
 */
export const products: Product[] = [
  /* =============================== SANDÁLIAS (5) ============================ */
  {
    id: "sandalia-plataforma-couro",
    code: "AUR-SAN-001",
    name: "Sandália Plataforma Couro",
    categoryId: "sandalias",
    category: "Sandálias",
    price: 299.9,
    installments: 3,
    colors: [
      {
        name: "Conhaque",
        hex: "#a86138",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cognac%20brown%20leather%20platform%20sandals%20with%20a%20tall%20chunky%20platform%20sole%20and%20two%20wide%20crossed%20straps%2C%20both%20shoes%20identical%20and%20fully%20visible%2C%20correct%20connected%20straps%20and%20clean%20sole%2C%20standing%20on%20a%20pale%20wood%20plank%20with%20soft%20raking%20daylight%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p01-plataforma-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20platform%20sandals%20with%20a%20tall%20chunky%20platform%20sole%20and%20two%20wide%20crossed%20straps%2C%20both%20shoes%20identical%20and%20fully%20visible%2C%20correct%20connected%20straps%20and%20clean%20sole%2C%20standing%20on%20a%20pale%20grey%20stone%20block%20with%20soft%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p01-plataforma-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["34"],
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 92,
    stock: 10,
    description:
      "Plataforma alta com tiras largas que dão sustentação ao pé. Levanta o look sem abrir mão do equilíbrio.",
    fabric: "Couro legítimo com plataforma de EVA revestida",
    fit: "Plataforma de 7 cm com tiras cruzadas",
    care: "Limpar com pano seco. Evitar contato prolongado com água.",
  },
  {
    id: "sandalia-salto-bloco-tiras",
    code: "AUR-SAN-002",
    name: "Sandália Salto Bloco com Tiras",
    categoryId: "sandalias",
    category: "Sandálias",
    price: 259.9,
    installments: 3,
    colors: [
      {
        name: "Bordô",
        hex: "#891F2D",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20bordeaux%20leather%20sandals%20with%20a%20square%20block%20heel%20and%20three%20thin%20crossed%20straps%20over%20the%20instep%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20block%20heel%2C%20straps%20properly%20attached%2C%20on%20a%20warm%20cream%20backdrop%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p02-bloco-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Animal print",
        hex: "#8b6b4a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20block%20heel%20sandals%20in%20brown%20animal%20print%20with%20three%20thin%20crossed%20straps%20over%20the%20instep%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20block%20heel%2C%20straps%20properly%20attached%2C%20on%20a%20beige%20stone%20slab%20with%20soft%20light%2C%20realistic%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p02-bloco-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: true,
    sold: 128,
    stock: 9,
    description:
      "Salto bloco quadrado com tiras finas cruzadas. Uma sandália curinga que combina com jeans e alfaiataria.",
    fabric: "Couro legítimo com forro macio",
    fit: "Salto bloco de 6 cm com tira de tornozelo",
    care: "Limpar com pano seco. Guardar no saco de proteção.",
  },
  {
    id: "sandalia-salto-fino-festa",
    code: "AUR-SAN-003",
    name: "Sandália Salto Fino Festa",
    categoryId: "sandalias",
    category: "Sandálias",
    price: 289.9,
    installments: 3,
    colors: [
      {
        name: "Vermelho cereja",
        hex: "#8e1f2b",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cherry%20red%20satin%20evening%20sandals%20with%20a%20slim%20stiletto%20heel%20and%20delicate%20ankle%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20fine%20heel%2C%20correctly%20connected%20straps%2C%20on%20a%20deep%20bordeaux%20surface%20with%20elegant%20side%20light%2C%20realistic%20satin%20sheen%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p03-festa-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Prata",
        hex: "#c9c9cf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20silver%20metallic%20evening%20sandals%20with%20a%20slim%20stiletto%20heel%20and%20delicate%20ankle%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20fine%20heel%2C%20correctly%20connected%20straps%2C%20on%20a%20dark%20grey%20glossy%20surface%20with%20cool%20side%20light%2C%20realistic%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p03-festa-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["40"],
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 41,
    stock: 7,
    description:
      "Salto fino com tira de tornozelo e brilho discreto. A sandália certa para festas, jantares e ocasiões especiais.",
    fabric: "Sintético premium com salto revestido",
    fit: "Salto fino de 9 cm com tira ajustável",
    care: "Guardar na caixa. Limpar com pano macio e seco.",
  },
  {
    id: "sandalia-anabela",
    code: "AUR-SAN-004",
    name: "Sandália Anabela",
    categoryId: "sandalias",
    category: "Sandálias",
    price: 239.9,
    installments: 3,
    colors: [
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20anabela%20wedge%20sandals%20with%20a%20closed%20heel%20and%20a%20wide%20front%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20wedge%2C%20properly%20attached%20strap%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p04-anabela-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20anabela%20wedge%20sandals%20with%20a%20closed%20heel%20and%20a%20wide%20front%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20wedge%2C%20properly%20attached%20strap%2C%20on%20a%20light%20beige%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p04-anabela-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 87,
    stock: 11,
    description:
      "Anabela com fechamento no calcanhar e tira larga que firma o pé. Conforto estável para o dia inteiro.",
    fabric: "Couro legítimo com forro macio",
    fit: "Anabela de 6 cm com fecho no calcanhar",
    care: "Limpar com pano seco. Evitar superfícies ásperas.",
  },
  {
    id: "sandalia-metalizada",
    code: "AUR-SAN-005",
    name: "Sandália Metalizada",
    categoryId: "sandalias",
    category: "Sandálias",
    price: 249.9,
    installments: 3,
    colors: [
      {
        name: "Dourado",
        hex: "#c9a15e",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20gold%20metallic%20strappy%20flat%20sandals%20with%20thin%20interlaced%20straps%20and%20a%20small%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20correctly%20connected%20straps%2C%20on%20a%20cream%20stone%20surface%20with%20warm%20directional%20light%2C%20reflective%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p05-metalizada-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Prata",
        hex: "#c9c9cf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20silver%20metallic%20strappy%20flat%20sandals%20with%20thin%20interlaced%20straps%20and%20a%20small%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20correctly%20connected%20straps%2C%20on%20a%20pale%20grey%20pebble%20surface%20with%20cool%20directional%20light%2C%20reflective%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p05-metalizada-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: true,
    sold: 58,
    stock: 12,
    description:
      "Tiras metalizadas que dão brilho ao pé em qualquer produção. Fácil de combinar com vestidos e alfaiataria.",
    fabric: "Couro metalizado com forro têxtil",
    fit: "Rasteira com fivela de tornozelo",
    care: "Guardar no saco de proteção. Limpar com pano macio.",
  },

  /* ================================ SCARPINS (4) =========================== */
  {
    id: "scarpin-slingback",
    code: "AUR-SCA-001",
    name: "Scarpin Slingback",
    categoryId: "scarpins",
    category: "Scarpins",
    price: 289.9,
    installments: 3,
    colors: [
      {
        name: "Vermelho cereja",
        hex: "#8e1f2b",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cherry%20red%20leather%20slingback%20pumps%20with%20a%20pointed%20closed%20toe%20and%20a%20slim%20back%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20mid%20heel%2C%20strap%20correctly%20attached%2C%20on%20a%20cream%20base%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p06-slingback-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Azul profundo",
        hex: "#1e3a5f",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20deep%20navy%20blue%20leather%20slingback%20pumps%20with%20a%20pointed%20closed%20toe%20and%20a%20slim%20back%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20mid%20heel%2C%20strap%20correctly%20attached%2C%20on%20a%20warm%20cream%20pedestal%20with%20soft%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p06-slingback-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 76,
    stock: 8,
    description:
      "Slingback de bico fino com tira de apoio atrás. Elegante como scarpin e confortável como sapatilha.",
    fabric: "Couro legítimo com palmilha acolchoada",
    fit: "Salto médio de 5 cm com tira traseira",
    care: "Limpar com pano macio. Guardar na caixa.",
  },
  {
    id: "scarpin-classico-salto-medio",
    code: "AUR-SCA-002",
    name: "Scarpin Clássico Salto Médio",
    categoryId: "scarpins",
    category: "Scarpins",
    price: 279.9,
    installments: 3,
    colors: [
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20closed%20toe%20classic%20pumps%20with%20a%20structurally%20correct%20mid%20heel%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20smooth%20leather%20grain%2C%20elegant%20minimal%20composition%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p07-classico-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20closed%20toe%20classic%20pumps%20with%20a%20structurally%20correct%20mid%20heel%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20light%20beige%20surface%20with%20soft%20natural%20light%2C%20realistic%20smooth%20leather%20grain%2C%20elegant%20minimal%20composition%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p07-classico-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["40"],
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 101,
    stock: 9,
    description:
      "O scarpin clássico de sempre, com salto médio que equilibra elegância e estabilidade. Um básico de peso no guarda-roupa.",
    fabric: "Couro legítimo com forro macio",
    fit: "Salto médio de 6 cm com base firme",
    care: "Limpar com pano seco. Evitar contato com água.",
  },
  {
    id: "scarpin-salto-baixo-laco",
    code: "AUR-SCA-003",
    name: "Scarpin Salto Baixo com Laço",
    categoryId: "scarpins",
    category: "Scarpins",
    price: 259.9,
    installments: 3,
    colors: [
      {
        name: "Off-white",
        hex: "#f2eadf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20off%20white%20leather%20pumps%20with%20a%20low%20kitten%20heel%20and%20a%20small%20tied%20bow%20detail%20on%20the%20toe%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20low%20heel%2C%20on%20a%20cream%20fabric%20base%20with%20soft%20light%2C%20realistic%20leather%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p08-laco-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20pumps%20with%20a%20low%20kitten%20heel%20and%20a%20small%20tied%20bow%20detail%20on%20the%20toe%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20low%20heel%2C%20on%20a%20warm%20beige%20surface%20with%20soft%20light%2C%20realistic%20leather%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p08-laco-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 38,
    stock: 10,
    description:
      "Salto baixo com laço delicado no bico. Um charme discreto para quem quer conforto sem perder a elegância.",
    fabric: "Couro legítimo com laço aplicado",
    fit: "Salto baixo de 3 cm com bico fechado",
    care: "Limpar com pano macio. Guardar na caixa.",
  },
  {
    id: "scarpin-bico-fino",
    code: "AUR-SCA-004",
    name: "Scarpin Bico Fino",
    categoryId: "scarpins",
    category: "Scarpins",
    price: 269.9,
    installments: 3,
    colors: [
      {
        name: "Azul médio",
        hex: "#3b6ea5",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20medium%20blue%20leather%20pointed%20toe%20pumps%20with%20a%20thin%20elegant%20stiletto%20heel%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20heel%2C%20on%20a%20cream%20stone%20pedestal%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p09-bicofino-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Vermelho cereja",
        hex: "#8e1f2b",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cherry%20red%20leather%20pointed%20toe%20pumps%20with%20a%20thin%20elegant%20stiletto%20heel%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20heel%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p09-bicofino-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 69,
    stock: 7,
    description:
      "Bico fino e cor intensa para um scarpin cheio de presença. Alonga a silhueta e fecha produções mais marcantes.",
    fabric: "Couro legítimo com palmilha acolchoada",
    fit: "Salto de 8 cm com bico fino",
    care: "Guardar na caixa. Limpar com pano macio.",
  },

  /* ============================== SAPATILHAS (4) =========================== */
  {
    id: "sapatilha-classica-bico-redondo",
    code: "AUR-SAP-001",
    name: "Sapatilha Clássica Bico Redondo",
    categoryId: "sapatilhas",
    category: "Sapatilhas",
    price: 199.9,
    installments: 3,
    colors: [
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20ballet%20flats%20with%20a%20rounded%20closed%20toe%20and%20a%20small%20bow%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20flexible%20sole%2C%20on%20a%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20smooth%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p10-classica-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20ballet%20flats%20with%20a%20rounded%20closed%20toe%20and%20a%20small%20bow%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20flexible%20sole%2C%20on%20a%20light%20beige%20surface%20with%20soft%20natural%20light%2C%20realistic%20smooth%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p10-classica-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["34"],
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 134,
    stock: 14,
    description:
      "Sapatilha de bico redondo com solado flexível e palmilha macia. A companheira de quem passa o dia em pé.",
    fabric: "Couro legítimo com palmilha acolchoada",
    fit: "Bico redondo com laço frontal",
    care: "Limpar com pano macio. Evitar superfícies ásperas.",
  },
  {
    id: "sapatilha-slingback",
    code: "AUR-SAP-002",
    name: "Sapatilha Slingback",
    categoryId: "sapatilhas",
    category: "Sapatilhas",
    price: 219.9,
    installments: 3,
    colors: [
      {
        name: "Azul-noite",
        hex: "#243b55",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20navy%20blue%20leather%20pointed%20slingback%20flats%20with%20a%20slim%20back%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20flexible%20sole%2C%20strap%20correctly%20attached%2C%20on%20a%20cream%20base%20against%20a%20deep%20bordeaux%20background%2C%20soft%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p11-sling-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20pointed%20slingback%20flats%20with%20a%20slim%20back%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20flexible%20sole%2C%20strap%20correctly%20attached%2C%20on%20a%20warm%20cream%20base%20with%20soft%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p11-sling-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: true,
    sold: 72,
    stock: 9,
    description:
      "Bico fino com tira de apoio atrás, sem salto. Deixa o dia de trabalho mais leve sem perder a elegância.",
    fabric: "Couro sintético premium com forro têxtil",
    fit: "Bico fino com solado fino flexível",
    care: "Limpar com pano macio. Evitar superfícies ásperas.",
  },
  {
    id: "sapatilha-tira-fivela",
    code: "AUR-SAP-003",
    name: "Sapatilha Tira e Fivela",
    categoryId: "sapatilhas",
    category: "Sapatilhas",
    price: 209.9,
    installments: 3,
    colors: [
      {
        name: "Animal print",
        hex: "#8b6b4a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20ballet%20flats%20in%20brown%20animal%20print%20with%20a%20thin%20strap%20across%20the%20instep%20and%20a%20small%20functional%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20sole%2C%20on%20a%20light%20stone%20surface%20with%20soft%20light%2C%20realistic%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p12-fivela-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20ballet%20flats%20with%20a%20thin%20strap%20across%20the%20instep%20and%20a%20small%20functional%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20sole%2C%20on%20a%20cream%20surface%20with%20soft%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p12-fivela-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 44,
    stock: 10,
    description:
      "Sapatilha com tira e fivela que ajusta o pé com firmeza. Bico arredondado e solado macio para o dia inteiro.",
    fabric: "Couro legítimo com fivela metálica",
    fit: "Bico arredondado com tira ajustável",
    care: "Limpar com pano seco. Guardar na caixa.",
  },
  {
    id: "sapatilha-tramada",
    code: "AUR-SAP-004",
    name: "Sapatilha Tramada",
    categoryId: "sapatilhas",
    category: "Sapatilhas",
    price: 229.9,
    installments: 3,
    colors: [
      {
        name: "Bege tramado",
        hex: "#d8c4a8",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20beige%20woven%20texture%20ballet%20flats%20with%20a%20rounded%20toe%20and%20delicate%20braided%20pattern%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20sole%2C%20on%20a%20cream%20linen%20surface%20with%20soft%20natural%20light%2C%20realistic%20woven%20material%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p13-tramada-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Café",
        hex: "#4a3428",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20coffee%20brown%20woven%20texture%20ballet%20flats%20with%20a%20rounded%20toe%20and%20delicate%20braided%20pattern%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20sole%2C%20on%20a%20warm%20beige%20surface%20with%20soft%20natural%20light%2C%20realistic%20woven%20material%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p13-tramada-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 51,
    stock: 11,
    description:
      "Material tramado leve e respirável, ideal para os dias mais quentes. Textura que valoriza qualquer look simples.",
    fabric: "Material tramado com forro têxtil",
    fit: "Bico arredondado com solado flexível",
    care: "Limpar com pano seco. Evitar contato com água.",
  },

  /* ================================= MULES (3) ============================= */
  {
    id: "mule-salto-baixo",
    code: "AUR-MUL-001",
    name: "Mule Salto Baixo",
    categoryId: "mules",
    category: "Mules",
    price: 229.9,
    installments: 3,
    colors: [
      {
        name: "Conhaque",
        hex: "#a86138",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cognac%20brown%20leather%20mules%20with%20a%20low%20block%20heel%20and%20an%20open%20back%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20open%20heel%20construction%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p14-mulebaixo-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20mules%20with%20a%20low%20block%20heel%20and%20an%20open%20back%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20open%20heel%20construction%2C%20on%20a%20pale%20grey%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p14-mulebaixo-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 96,
    stock: 12,
    description:
      "Mule de salto baixo que entra e sai do pé num segundo. Prático para o dia a dia e elegante na hora de sair.",
    fabric: "Couro legítimo com forro macio",
    fit: "Salto baixo de 4 cm sem contraforte",
    care: "Limpar com pano seco. Guardar na caixa.",
  },
  {
    id: "mule-salto-fino-detalhe",
    code: "AUR-MUL-002",
    name: "Mule Salto Fino com Detalhe",
    categoryId: "mules",
    category: "Mules",
    price: 249.9,
    installments: 3,
    colors: [
      {
        name: "Vermelho cereja",
        hex: "#8e1f2b",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cherry%20red%20leather%20mules%20with%20a%20slim%20stiletto%20heel%20and%20a%20small%20metallic%20detail%20on%20the%20vamp%2C%20open%20back%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20heel%2C%20on%20a%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p15-mulefino-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20mules%20with%20a%20slim%20stiletto%20heel%20and%20a%20small%20metallic%20detail%20on%20the%20vamp%2C%20open%20back%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20heel%2C%20on%20a%20light%20beige%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p15-mulefino-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 37,
    stock: 8,
    description:
      "Salto fino com um detalhe metálico discreto que valoriza o modelo. Fácil de calçar e cheio de personalidade.",
    fabric: "Couro legítimo com detalhe metálico",
    fit: "Salto fino de 7 cm sem contraforte",
    care: "Guardar na caixa. Limpar com pano macio.",
  },
  {
    id: "mule-anabela",
    code: "AUR-MUL-003",
    name: "Mule Anabela",
    categoryId: "mules",
    category: "Mules",
    price: 239.9,
    installments: 3,
    colors: [
      {
        name: "Bege",
        hex: "#d8c4a8",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20beige%20leather%20anabela%20wedge%20mules%20with%20an%20open%20back%20and%20a%20closed%20front%20toe%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20wedge%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p16-muleanabela-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Azul médio",
        hex: "#3b6ea5",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20medium%20blue%20leather%20anabela%20wedge%20mules%20with%20an%20open%20back%20and%20a%20closed%20front%20toe%2C%20both%20shoes%20identical%20and%20whole%2C%20structurally%20correct%20wedge%2C%20on%20a%20pale%20stone%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p16-muleanabela-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 48,
    stock: 9,
    description:
      "Anabela em formato mule, com bico fechado e calcanhar livre. Sobe o look com conforto estável.",
    fabric: "Couro legítimo com plataforma revestida",
    fit: "Anabela de 6 cm sem contraforte",
    care: "Limpar com pano seco. Evitar superfícies irregulares.",
  },

  /* =============================== MOCASSINS (3) =========================== */
  {
    id: "mocassim-classico",
    code: "AUR-MOC-001",
    name: "Mocassim Clássico",
    categoryId: "mocassins",
    category: "Mocassins",
    price: 249.9,
    installments: 3,
    colors: [
      {
        name: "Café",
        hex: "#4a3428",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20coffee%20brown%20leather%20loafers%20with%20a%20low%20stacked%20heel%20and%20a%20classic%20rounded%20vamp%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20smooth%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p17-mocclassico-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20loafers%20with%20a%20low%20stacked%20heel%20and%20a%20classic%20rounded%20vamp%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20pale%20grey%20surface%20with%20soft%20natural%20light%2C%20realistic%20smooth%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p17-mocclassico-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["40"],
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 89,
    stock: 10,
    description:
      "Mocassim de couro com salto baixo e caimento firme. Um clássico que vai do escritório ao fim de tarde.",
    fabric: "Couro legítimo com forro macio",
    fit: "Salto baixo de 2 cm com palmilha macia",
    care: "Guardar em local seco. Limpar com pano macio.",
  },
  {
    id: "mocassim-tratorado",
    code: "AUR-MOC-002",
    name: "Mocassim Tratorado",
    categoryId: "mocassins",
    category: "Mocassins",
    price: 289.9,
    installments: 3,
    colors: [
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20lug%20sole%20loafers%20with%20a%20chunky%20tractor%20sole%20and%20a%20metal%20chain%20detail%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20heavy%20sole%2C%20on%20a%20concrete%20surface%20with%20assertive%20side%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p18-tratorado-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20chocolate%20brown%20leather%20lug%20sole%20loafers%20with%20a%20chunky%20tractor%20sole%20and%20a%20metal%20chain%20detail%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20heavy%20sole%2C%20on%20a%20grey%20stone%20surface%20with%20assertive%20side%20light%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p18-tratorado-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 45,
    stock: 8,
    description:
      "Solado tratorado com corrente metálica: moderno e cheio de atitude. Agarra bem o chão em qualquer caminhada.",
    fabric: "Couro legítimo com solado tractor",
    fit: "Solado alto com palmilha acolchoada",
    care: "Limpar com escova macia e pano seco.",
  },
  {
    id: "mocassim-aplicacao-metalica",
    code: "AUR-MOC-003",
    name: "Mocassim com Aplicação Metálica",
    categoryId: "mocassins",
    category: "Mocassins",
    price: 259.9,
    installments: 3,
    colors: [
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20chocolate%20brown%20leather%20loafers%20with%20a%20large%20golden%20metallic%20snaffle%20bar%20across%20the%20vamp%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p19-mocmetal-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Bicolor",
        hex: "#c9b8a0",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20two%20tone%20leather%20loafers%20in%20off%20white%20and%20chocolate%20brown%20with%20a%20golden%20metallic%20snaffle%20bar%20across%20the%20vamp%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20light%20beige%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p19-mocmetal-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 53,
    stock: 9,
    description:
      "Mocassim com barra metálica dourada que dá um acabamento sofisticado. Combina com alfaiataria e jeans.",
    fabric: "Couro legítimo com aplicação metálica",
    fit: "Salto baixo de 3 cm com bico arredondado",
    care: "Limpar com pano macio. Guardar na caixa.",
  },

  /* ================================= TÊNIS (3) ============================= */
  {
    id: "tenis-casual-couro",
    code: "AUR-TEN-001",
    name: "Tênis Casual Couro",
    categoryId: "tenis",
    category: "Tênis",
    price: 299.9,
    installments: 3,
    colors: [
      {
        name: "Conhaque",
        hex: "#a86138",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cognac%20brown%20leather%20casual%20sneakers%20with%20a%20cream%20rubber%20sole%20and%20clean%20laces%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20laces%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p20-casual-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Off-white",
        hex: "#f2eadf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20off%20white%20leather%20casual%20sneakers%20with%20a%20soft%20gum%20sole%20and%20clean%20laces%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20laces%2C%20on%20a%20light%20beige%20surface%20with%20soft%20natural%20light%2C%20realistic%20leather%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p20-casual-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 118,
    stock: 9,
    description:
      "Couro fosco e solado claro que amacia com o uso. Um casual que acompanha o dia inteiro sem pesar.",
    fabric: "Couro legítimo com solado de borracha",
    fit: "Solado leve com palmilha macia",
    care: "Limpar com escova macia e pano seco.",
  },
  {
    id: "tenis-urbano-bicolor",
    code: "AUR-TEN-002",
    name: "Tênis Urbano Bicolor",
    categoryId: "tenis",
    category: "Tênis",
    price: 279.9,
    installments: 3,
    colors: [
      {
        name: "Azul e branco",
        hex: "#3b6ea5",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20two%20tone%20urban%20sneakers%20in%20white%20and%20medium%20blue%20leather%20with%20clean%20laces%20and%20a%20slim%20white%20sole%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20light%20concrete%20surface%20with%20soft%20daylight%2C%20realistic%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p21-bicolor-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto e branco",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20two%20tone%20urban%20sneakers%20in%20black%20and%20white%20leather%20with%20clean%20laces%20and%20a%20slim%20white%20sole%2C%20both%20shoes%20identical%20and%20whole%2C%20on%20a%20pale%20grey%20concrete%20surface%20with%20soft%20daylight%2C%20realistic%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p21-bicolor-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["40"],
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 62,
    stock: 8,
    description:
      "Bicolor com solado fino que combina com jeans e alfaiataria. Um urbano limpo para o dia a dia.",
    fabric: "Couro sintético de alta qualidade com palmilha acolchoada",
    fit: "Numeração fiel ao padrão brasileiro",
    care: "Limpar com pano úmido e sabão neutro. Secar à sombra.",
  },
  {
    id: "tenis-plataforma",
    code: "AUR-TEN-003",
    name: "Tênis Plataforma",
    categoryId: "tenis",
    category: "Tênis",
    price: 319.9,
    installments: 3,
    colors: [
      {
        name: "Off-white",
        hex: "#f2eadf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20off%20white%20leather%20platform%20sneakers%20with%20a%20thick%20chunky%20sole%20and%20clean%20laces%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20thick%20sole%20and%20laces%2C%20on%20a%20cream%20surface%20with%20soft%20daylight%2C%20realistic%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p22-plataforma-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20platform%20sneakers%20with%20a%20thick%20chunky%20sole%20and%20clean%20laces%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20thick%20sole%20and%20laces%2C%20on%20a%20charcoal%20grey%20surface%20with%20soft%20daylight%2C%20realistic%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p22-plataforma-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 74,
    stock: 7,
    description:
      "Plataforma que dá altura e presença sem saltos. Solado firme e visual contemporâneo para a cidade.",
    fabric: "Couro legítimo com solado plataforma",
    fit: "Plataforma de 5 cm com palmilha macia",
    care: "Limpar com escova macia. Evitar contato com água.",
  },

  /* =============================== RASTEIRAS (3) =========================== */
  {
    id: "rasteira-minimalista",
    code: "AUR-RAS-001",
    name: "Rasteira Minimalista",
    categoryId: "rasteiras",
    category: "Rasteiras",
    price: 149.9,
    installments: 3,
    colors: [
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20nude%20leather%20minimalist%20flat%20sandals%20with%20two%20clean%20thin%20straps%20and%20a%20flat%20sole%2C%20both%20shoes%20identical%20and%20whole%2C%20connected%20straps%2C%20on%20a%20light%20natural%20stone%20slab%20with%20warm%20daylight%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p23-minimal-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20minimalist%20flat%20sandals%20with%20two%20clean%20thin%20straps%20and%20a%20flat%20sole%2C%20both%20shoes%20identical%20and%20whole%2C%20connected%20straps%2C%20on%20a%20pale%20concrete%20surface%20with%20warm%20daylight%2C%20realistic%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p23-minimal-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizesOut: ["34", "40"],
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 121,
    stock: 15,
    description:
      "Duas tiras finas e nada mais: leve, fresca e fácil de combinar. A rasteira que resolve os dias quentes.",
    fabric: "Couro legítimo com solado flexível",
    fit: "Rasteira de duas tiras com solado fino",
    care: "Evitar exposição prolongada ao sol. Limpar com pano seco.",
  },
  {
    id: "rasteira-fivela",
    code: "AUR-RAS-002",
    name: "Rasteira com Fivela",
    categoryId: "rasteiras",
    category: "Rasteiras",
    price: 179.9,
    installments: 3,
    colors: [
      {
        name: "Dourado",
        hex: "#c9a15e",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20gold%20metallic%20flat%20sandals%20with%20a%20wide%20front%20strap%20and%20a%20large%20functional%20round%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20buckle%2C%20on%20a%20cream%20stone%20surface%20with%20warm%20light%2C%20realistic%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p24-fivela-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Café",
        hex: "#4a3428",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20coffee%20brown%20leather%20flat%20sandals%20with%20a%20wide%20front%20strap%20and%20a%20large%20functional%20round%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20correct%20buckle%2C%20on%20a%20warm%20beige%20surface%20with%20warm%20light%2C%20realistic%20leather%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p24-fivela-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: true,
    sold: 55,
    stock: 12,
    description:
      "Fivela redonda em destaque e tira larga no peito do pé. Conforto firme para caminhar pela cidade.",
    fabric: "Couro legítimo com fivela metálica",
    fit: "Rasteira com fivela ajustável",
    care: "Limpar com pano macio e seco.",
  },
  {
    id: "rasteira-tramada",
    code: "AUR-RAS-003",
    name: "Rasteira Tramada",
    categoryId: "rasteiras",
    category: "Rasteiras",
    price: 169.9,
    installments: 3,
    colors: [
      {
        name: "Areia tramada",
        hex: "#d8c4a8",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20areia%20woven%20braided%20flat%20sandals%20with%20a%20single%20connected%20toe%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20flexible%20sole%2C%20on%20a%20light%20natural%20stone%20surface%20with%20warm%20sunlight%2C%20realistic%20woven%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p25-tramada-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20chocolate%20brown%20woven%20braided%20flat%20sandals%20with%20a%20single%20connected%20toe%20strap%2C%20both%20shoes%20identical%20and%20whole%2C%20flat%20flexible%20sole%2C%20on%20a%20warm%20sand%20surface%20with%20warm%20sunlight%2C%20realistic%20woven%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p25-tramada-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 49,
    stock: 13,
    description:
      "Trama trançada com tira única que não machuca o pé. Leve, fresca e cheia de textura.",
    fabric: "Couro legítimo trançado",
    fit: "Rasteira com tira única",
    care: "Evitar exposição prolongada ao sol. Limpar com pano seco.",
  },

  /* ================================ PAPETES (3) ============================ */
  {
    id: "papete-couro",
    code: "AUR-PAP-001",
    name: "Papete em Couro",
    categoryId: "papetes",
    category: "Papetes",
    price: 199.9,
    installments: 3,
    colors: [
      {
        name: "Caramelo",
        hex: "#b0703c",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20caramel%20leather%20papete%20flat%20sandals%20with%20a%20wide%20instep%20strap%20and%20a%20closed%20back%20with%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20comfortable%20closed%20heel%2C%20on%20a%20light%20natural%20stone%20surface%20with%20warm%20sunlight%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p26-papete-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20papete%20flat%20sandals%20with%20a%20wide%20instep%20strap%20and%20a%20closed%20back%20with%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20comfortable%20closed%20heel%2C%20on%20a%20pale%20grey%20surface%20with%20warm%20sunlight%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p26-papete-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Mais vendido",
    isNew: false,
    sold: 88,
    stock: 12,
    description:
      "Papete de couro com tira larga no peito do pé e fecho no calcanhar. Conforto firme para caminhar pela cidade.",
    fabric: "Couro legítimo com solado colado",
    fit: "Papete com fivela de calcanhar",
    care: "Limpar com pano macio e seco.",
  },
  {
    id: "papete-fivelas",
    code: "AUR-PAP-002",
    name: "Papete com Fivelas",
    categoryId: "papetes",
    category: "Papetes",
    price: 219.9,
    installments: 3,
    colors: [
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20chocolate%20brown%20leather%20papete%20sandals%20with%20three%20adjustable%20straps%20and%20three%20metal%20buckles%2C%20both%20shoes%20identical%20and%20whole%2C%20all%20buckles%20functional%2C%20on%20a%20light%20stone%20surface%20with%20warm%20light%2C%20realistic%20leather%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p27-fivelas-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Bege",
        hex: "#d8c4a8",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20beige%20leather%20papete%20sandals%20with%20three%20adjustable%20straps%20and%20three%20metal%20buckles%2C%20both%20shoes%20identical%20and%20whole%2C%20all%20buckles%20functional%2C%20on%20a%20warm%20sand%20surface%20with%20warm%20light%2C%20realistic%20leather%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p27-fivelas-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 47,
    stock: 10,
    description:
      "Três fivelas que ajustam o pé com precisão. Um modelo robusto e cheio de personalidade para o dia a dia.",
    fabric: "Couro legítimo com fivelas metálicas",
    fit: "Papete com três fivelas ajustáveis",
    care: "Limpar com pano seco. Guardar na caixa.",
  },
  {
    id: "papete-metalizada",
    code: "AUR-PAP-003",
    name: "Papete Metalizada",
    categoryId: "papetes",
    category: "Papetes",
    price: 229.9,
    installments: 3,
    colors: [
      {
        name: "Dourado",
        hex: "#c9a15e",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20gold%20metallic%20papete%20sandals%20with%20a%20wide%20strap%20and%20a%20small%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20closed%20heel%2C%20on%20a%20cream%20surface%20with%20warm%20directional%20light%2C%20reflective%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p28-papetemetal-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Prata",
        hex: "#c9c9cf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20silver%20metallic%20papete%20sandals%20with%20a%20wide%20strap%20and%20a%20small%20buckle%2C%20both%20shoes%20identical%20and%20whole%2C%20closed%20heel%2C%20on%20a%20pale%20grey%20surface%20with%20cool%20directional%20light%2C%20reflective%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p28-papetemetal-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 36,
    stock: 9,
    description:
      "Papete metalizada que dá brilho ao look de verão. Confortável e fácil de combinar do dia à noite.",
    fabric: "Couro metalizado com fivela metálica",
    fit: "Papete com fivela de calcanhar",
    care: "Guardar no saco de proteção. Limpar com pano macio.",
  },

  /* ================================= BOTAS (2) ============================= */
  {
    id: "bota-cano-curto",
    code: "AUR-BOT-001",
    name: "Bota de Cano Curto",
    categoryId: "botas",
    category: "Botas",
    price: 349.9,
    installments: 3,
    colors: [
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20chocolate%20brown%20leather%20ankle%20boots%20with%20a%20short%20shaft%20and%20a%20low%20block%20heel%2C%20both%20boots%20identical%20and%20whole%2C%20correct%20shaft%20and%20heel%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p29-cano-curto-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20black%20leather%20ankle%20boots%20with%20a%20short%20shaft%20and%20a%20low%20block%20heel%2C%20both%20boots%20identical%20and%20whole%2C%20correct%20shaft%20and%20heel%2C%20on%20a%20pale%20grey%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p29-cano-curto-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Destaque",
    isNew: false,
    sold: 64,
    stock: 8,
    description:
      "Bota de cano curto com salto bloco baixo. Fecha looks de meia-estação com conforto e estilo.",
    fabric: "Couro legítimo com forro têxtil",
    fit: "Cano curto com salto bloco de 4 cm",
    care: "Limpar com pano seco. Guardar na caixa.",
  },
  {
    id: "bota-salto-bloco",
    code: "AUR-BOT-002",
    name: "Bota de Salto Bloco",
    categoryId: "botas",
    category: "Botas",
    price: 379.9,
    installments: 3,
    colors: [
      {
        name: "Conhaque",
        hex: "#a86138",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20cognac%20brown%20leather%20boots%20with%20a%20tall%20block%20heel%20and%20a%20mid%20calf%20shaft%2C%20both%20boots%20identical%20and%20whole%2C%20structurally%20correct%20block%20heel%2C%20on%20a%20warm%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p30-bota-bloco-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Café",
        hex: "#4a3428",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20single%20pair%20of%20womens%20coffee%20brown%20leather%20boots%20with%20a%20tall%20block%20heel%20and%20a%20mid%20calf%20shaft%2C%20both%20boots%20identical%20and%20whole%2C%20structurally%20correct%20block%20heel%2C%20on%20a%20warm%20beige%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p30-bota-bloco-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: SHOE_SIZES,
    sizeGroup: "shoes",
    tag: "Novo",
    isNew: true,
    sold: 42,
    stock: 7,
    description:
      "Bota de salto bloco com cano na altura da panturrilha. Segura o passo com firmeza e alonga a silhueta.",
    fabric: "Couro legítimo com salto revestido",
    fit: "Salto bloco de 7 cm com fecho lateral",
    care: "Guardar na caixa com enchimento. Limpar com pano macio.",
  },

  /* ================================= BOLSAS (6) ============================ */
  {
    id: "bolsa-mao-estruturada",
    code: "AUR-BOL-001",
    name: "Bolsa de Mão Estruturada",
    categoryId: "bolsas",
    category: "Bolsas",
    price: 349.9,
    installments: 3,
    colors: [
      {
        name: "Conhaque",
        hex: "#a86138",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20structured%20cognac%20brown%20leather%20handbag%20with%20a%20clean%20trapezoid%20body%2C%20a%20rigid%20top%20handle%20and%20a%20functional%20metal%20clasp%2C%20standing%20upright%20on%20a%20warm%20cream%20surface%20with%20soft%20architectural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p31-estruturada-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Azul profundo",
        hex: "#1e3a5f",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20structured%20deep%20navy%20blue%20leather%20handbag%20with%20a%20clean%20trapezoid%20body%2C%20a%20rigid%20top%20handle%20and%20a%20functional%20metal%20clasp%2C%20standing%20upright%20on%20a%20pale%20stone%20surface%20with%20soft%20architectural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p31-estruturada-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Mais vendido",
    isNew: false,
    sold: 81,
    stock: 7,
    description:
      "Estrutura firme que não deixa a bolsa caída, com alça de mão e divisórias internas. Organiza o dia inteiro.",
    fabric: "Couro legítimo com forro interno",
    fit: "Alça de mão e bolso interno",
    care: "Guardar preenchida. Limpar com pano macio e seco.",
  },
  {
    id: "bolsa-ombro",
    code: "AUR-BOL-002",
    name: "Bolsa de Ombro",
    categoryId: "bolsas",
    category: "Bolsas",
    price: 319.9,
    installments: 3,
    colors: [
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20soft%20chocolate%20brown%20leather%20shoulder%20bag%20with%20a%20medium%20crescent%20shape%20and%20a%20single%20wide%20shoulder%20strap%2C%20hanging%20naturally%20on%20a%20warm%20cream%20surface%20with%20soft%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p32-ombro-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Off-white",
        hex: "#f2eadf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20soft%20off%20white%20leather%20shoulder%20bag%20with%20a%20medium%20crescent%20shape%20and%20a%20single%20wide%20shoulder%20strap%2C%20hanging%20naturally%20on%20a%20light%20beige%20surface%20with%20soft%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p32-ombro-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Destaque",
    isNew: false,
    sold: 59,
    stock: 8,
    description:
      "Alça de ombro larga que distribui o peso e não marca a roupa. Espaço na medida para o essencial.",
    fabric: "Couro legítimo com alça reforçada",
    fit: "Alça de ombro fixa e bolso interno",
    care: "Guardar preenchida. Limpar com pano seco.",
  },
  {
    id: "bolsa-transversal",
    code: "AUR-BOL-003",
    name: "Bolsa Transversal",
    categoryId: "bolsas",
    category: "Bolsas",
    price: 269.9,
    installments: 3,
    colors: [
      {
        name: "Azul profundo",
        hex: "#1e3a5f",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20small%20deep%20navy%20blue%20leather%20crossbody%20bag%20with%20a%20rounded%20structured%20shape%20and%20a%20long%20thin%20adjustable%20crossbody%20strap%20clearly%20visible%2C%20standing%20on%20a%20warm%20cream%20surface%20with%20soft%20light%2C%20realistic%20leather%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p33-transversal-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Bege",
        hex: "#d8c4a8",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20small%20beige%20leather%20crossbody%20bag%20with%20a%20rounded%20structured%20shape%20and%20a%20long%20thin%20adjustable%20crossbody%20strap%20clearly%20visible%2C%20standing%20on%20a%20light%20stone%20surface%20with%20soft%20light%2C%20realistic%20leather%20texture%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p33-transversal-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Novo",
    isNew: true,
    sold: 54,
    stock: 9,
    description:
      "Compacta e prática, com alça transversal ajustável para usar no ombro ou atravessada. Mãos livres o dia todo.",
    fabric: "Couro legítimo com alça ajustável",
    fit: "Alça transversal regulável e bolso interno",
    care: "Evitar contato com água. Limpar com pano seco.",
  },
  {
    id: "bolsa-hobo",
    code: "AUR-BOL-004",
    name: "Bolsa Hobo",
    categoryId: "bolsas",
    category: "Bolsas",
    price: 299.9,
    installments: 3,
    colors: [
      {
        name: "Café",
        hex: "#4a3428",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20slouchy%20coffee%20brown%20leather%20hobo%20bag%20with%20a%20soft%20pliable%20crescent%20body%20and%20a%20single%20short%20handle%2C%20slightly%20draped%20on%20a%20warm%20cream%20surface%20with%20soft%20light%2C%20realistic%20soft%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p34-hobo-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Nude",
        hex: "#d9b79a",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20slouchy%20nude%20leather%20hobo%20bag%20with%20a%20soft%20pliable%20crescent%20body%20and%20a%20single%20short%20handle%2C%20slightly%20draped%20on%20a%20light%20beige%20surface%20with%20soft%20light%2C%20realistic%20soft%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p34-hobo-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Destaque",
    isNew: false,
    sold: 47,
    stock: 9,
    description:
      "Formato maleável que se ajusta ao corpo, com muito espaço interno. Perfeita para quem leva a vida na bolsa.",
    fabric: "Couro legítimo macio com forro",
    fit: "Alça curta e bolso interno",
    care: "Guardar preenchida. Limpar com pano macio e seco.",
  },
  {
    id: "bolsa-tote",
    code: "AUR-BOL-005",
    name: "Bolsa Tote",
    categoryId: "bolsas",
    category: "Bolsas",
    price: 369.9,
    installments: 3,
    colors: [
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20large%20chocolate%20brown%20leather%20tote%20bag%20with%20two%20long%20parallel%20handles%20and%20a%20wide%20rectangular%20body%2C%20standing%20upright%20on%20a%20warm%20cream%20surface%20with%20soft%20architectural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p35-tote-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Off-white",
        hex: "#f2eadf",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20large%20off%20white%20leather%20tote%20bag%20with%20two%20long%20parallel%20handles%20and%20a%20wide%20rectangular%20body%2C%20standing%20upright%20on%20a%20light%20stone%20surface%20with%20soft%20architectural%20light%2C%20realistic%20leather%20grain%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p35-tote-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Mais vendido",
    isNew: false,
    sold: 76,
    stock: 6,
    description:
      "Espaçosa e firme, cabe notebook, agenda e o que mais precisar. A tote do trabalho e do dia a dia.",
    fabric: "Couro legítimo estruturado com forro",
    fit: "Duas alças de mão e compartimentos internos",
    care: "Guardar preenchida. Limpar com pano seco.",
  },
  {
    id: "mini-bag-noite",
    code: "AUR-BOL-006",
    name: "Mini Bag para Noite",
    categoryId: "bolsas",
    category: "Bolsas",
    price: 239.9,
    installments: 3,
    colors: [
      {
        name: "Dourado",
        hex: "#c9a15e",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20small%20gold%20metallic%20evening%20mini%20bag%20with%20a%20boxy%20shape%20and%20a%20thin%20delicate%20chain%20strap%2C%20standing%20on%20a%20cream%20surface%20with%20elegant%20low%20light%2C%20reflective%20metallic%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p36-minibag-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20small%20black%20leather%20evening%20mini%20bag%20with%20a%20boxy%20shape%20and%20a%20thin%20delicate%20chain%20strap%2C%20standing%20on%20a%20dark%20grey%20surface%20with%20elegant%20low%20light%2C%20realistic%20leather%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p36-minibag-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Novo",
    isNew: true,
    sold: 39,
    stock: 10,
    description:
      "Pequena, mas cheia de presença, com correntinha fina para usar no ombro. A companheira das noites especiais.",
    fabric: "Sintético premium com corrente metálica",
    fit: "Corrente de ombro e fecho de clique",
    care: "Guardar no saco de proteção. Limpar com pano macio.",
  },

  /* ============================== ACESSÓRIOS (4) =========================== */
  {
    id: "cinto-fino",
    code: "AUR-CIN-001",
    name: "Cinto Fino",
    categoryId: "cintos",
    category: "Cintos",
    price: 129.9,
    installments: 3,
    colors: [
      {
        name: "Preto",
        hex: "#232323",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20thin%20black%20leather%20belt%20with%20a%20small%20rectangular%20metal%20buckle%2C%20neatly%20coiled%20into%20a%20circle%20on%20a%20warm%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20smooth%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p37-cintofino-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Café",
        hex: "#4a3428",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20thin%20coffee%20brown%20leather%20belt%20with%20a%20small%20rectangular%20metal%20buckle%2C%20neatly%20coiled%20into%20a%20circle%20on%20a%20light%20beige%20surface%20with%20soft%20side%20light%2C%20realistic%20smooth%20leather%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p37-cintofino-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Destaque",
    isNew: false,
    sold: 71,
    stock: 14,
    description:
      "Fino e discreto, marca a cintura sem roubar a cena. Ideal para usar sobre vestidos e alfaiataria.",
    fabric: "Couro legítimo com fivela metálica",
    fit: "Cintura regulável com furos",
    care: "Limpar com pano macio. Guardar enrolado.",
  },
  {
    id: "cinto-largo-fivela-organica",
    code: "AUR-CIN-002",
    name: "Cinto Largo com Fivela Orgânica",
    categoryId: "cintos",
    category: "Cintos",
    price: 179.9,
    installments: 3,
    colors: [
      {
        name: "Conhaque",
        hex: "#a86138",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20wide%20cognac%20brown%20leather%20belt%20with%20a%20large%20organic%20shaped%20golden%20buckle%2C%20laid%20flat%20with%20a%20gentle%20curve%20on%20a%20warm%20cream%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p38-cintolargo-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Marrom chocolate",
        hex: "#3a2418",
        image:
          "https://readdy.ai/api/search-image?query=Studio%20product%20photo%20of%20a%20wide%20chocolate%20brown%20leather%20belt%20with%20a%20large%20organic%20shaped%20golden%20buckle%2C%20laid%20flat%20with%20a%20gentle%20curve%20on%20a%20warm%20beige%20surface%20with%20soft%20side%20light%2C%20realistic%20leather%20grain%20and%20metal%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p38-cintolargo-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Novo",
    isNew: true,
    sold: 44,
    stock: 11,
    description:
      "Fivela orgânica dourada que transforma um cinto em detalhe de estilo. Largo, firme e bem acabado.",
    fabric: "Couro legítimo com fivela de metal envelhecido",
    fit: "Cintura regulável com furos",
    care: "Limpar com pano seco. Guardar enrolado.",
  },
  {
    id: "brinco-escultural",
    code: "AUR-SEM-001",
    name: "Brinco Escultural",
    categoryId: "semijoias",
    category: "Semijoias",
    price: 109.9,
    installments: 3,
    colors: [
      {
        name: "Dourado",
        hex: "#c9a15e",
        image:
          "https://readdy.ai/api/search-image?query=Macro%20studio%20product%20photo%20of%20a%20pair%20of%20sculptural%20gold%20plated%20statement%20earrings%20with%20smooth%20curved%20shapes%2C%20both%20perfectly%20symmetrical%20and%20shown%20together%20on%20a%20light%20stone%20slab%20with%20soft%20warm%20light%2C%20realistic%20metal%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p39-brinco-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Prata",
        hex: "#c9c9cf",
        image:
          "https://readdy.ai/api/search-image?query=Macro%20studio%20product%20photo%20of%20a%20pair%20of%20sculptural%20silver%20plated%20statement%20earrings%20with%20smooth%20curved%20shapes%2C%20both%20perfectly%20symmetrical%20and%20shown%20together%20on%20a%20pale%20grey%20surface%20with%20soft%20cool%20light%2C%20realistic%20metal%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p39-brinco-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Destaque",
    isNew: false,
    sold: 62,
    stock: 16,
    description:
      "Brinco de formato escultural que vira o ponto de destaque da produção. Leve para usar o dia inteiro.",
    fabric: "Semijoia com banho dourado suave",
    fit: "Tamanho único com fecho de encaixe",
    care: "Evitar contato com perfume, água e produtos de limpeza.",
  },
  {
    id: "colar-delicado",
    code: "AUR-SEM-002",
    name: "Colar Delicado",
    categoryId: "semijoias",
    category: "Semijoias",
    price: 119.9,
    installments: 3,
    colors: [
      {
        name: "Dourado",
        hex: "#c9a15e",
        image:
          "https://readdy.ai/api/search-image?query=Macro%20studio%20product%20photo%20of%20a%20thin%20delicate%20gold%20plated%20necklace%20with%20a%20single%20small%20clear%20light%20point%20pendant%2C%20laid%20in%20a%20soft%20curve%20on%20a%20light%20stone%20surface%20with%20warm%20soft%20light%2C%20realistic%20fine%20metal%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p40-colar-c1&orientation=portrait&nocache=true",
      },
      {
        name: "Prata",
        hex: "#c9c9cf",
        image:
          "https://readdy.ai/api/search-image?query=Macro%20studio%20product%20photo%20of%20a%20thin%20delicate%20silver%20plated%20necklace%20with%20a%20single%20small%20clear%20light%20point%20pendant%2C%20laid%20in%20a%20soft%20curve%20on%20a%20pale%20grey%20surface%20with%20cool%20soft%20light%2C%20realistic%20fine%20metal%20finish%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=700&height=900&seq=aur-p40-colar-c2&orientation=portrait&nocache=true",
      },
    ],
    sizes: ACCESSORY_SIZES,
    sizeGroup: "accessory",
    tag: "Destaque",
    isNew: false,
    sold: 58,
    stock: 15,
    description:
      "Fio fino com um único ponto de luz, perfeito para sobrepor outros colares. Delicado para todos os dias.",
    fabric: "Semijoia com banho suave e ponto de zircônia",
    fit: "Corrente ajustável com extensor",
    care: "Guardar separado. Evitar contato com água e perfume.",
  },
];

export const productById = (id: string): Product | undefined =>
  products.find((product) => product.id === id);

export const productsByCategory = (categoryId: string): Product[] =>
  products.filter((product) => product.categoryId === categoryId);

export const newProducts = (): Product[] => products.filter((product) => product.isNew);